import { useState } from 'react'
import type { ReactNode } from 'react'
import { Drawer } from '@/design-system/components/Drawer/Drawer'

export interface ConfirmationDialogProps {
  title: string
  description?: ReactNode
  confirmLabel: string
  pendingLabel?: string
  /** Étiquette du motif; sa saisie conditionne la confirmation. */
  reasonLabel?: string
  /**
   * Un motif écrit est exigé par défaut. On ne le retire que pour une action
   * sans trace à laisser — un ajout refait en trois secondes.
   */
  requireReason?: boolean
  reasonPlaceholder?: string
  /** Longueur maximale du motif ; affiche aussi un compteur de caractères. */
  maxReasonLength?: number
  pending?: boolean
  error?: ReactNode
  onConfirm: (reason: string) => void
  onClose: () => void
}

/**
 * Confirmation d'une action destructrice ou difficilement réversible.
 *
 * Reprend le pattern déjà éprouvé sur l'annulation de facture : un motif écrit
 * est exigé, et le bouton reste inerte tant qu'il manque. Écrire pourquoi
 * oblige à s'arrêter une seconde, et laisse une trace lisible à qui relira le
 * dossier.
 */
export function ConfirmationDialog({
  title,
  description,
  confirmLabel,
  pendingLabel,
  reasonLabel = 'Motif',
  reasonPlaceholder,
  maxReasonLength,
  requireReason = true,
  pending = false,
  error,
  onConfirm,
  onClose,
}: ConfirmationDialogProps) {
  const [reason, setReason] = useState('')
  const ready = !requireReason || reason.trim() !== ''

  return (
    <Drawer title={title} onClose={onClose}>
      <div className="ds-confirmation">
        {description ? <p>{description}</p> : null}
        {requireReason ? (
          <label className="ds-confirmation__reason">
            <span>{reasonLabel}</span>
            <textarea
              rows={3}
              value={reason}
              {...(reasonPlaceholder ? { placeholder: reasonPlaceholder } : {})}
              {...(maxReasonLength ? { maxLength: maxReasonLength } : {})}
              onChange={(event) => {
                setReason(event.target.value)
              }}
            />
            {maxReasonLength ? (
              <span className="ds-confirmation__reason-count">
                {reason.length}/{maxReasonLength}
              </span>
            ) : null}
          </label>
        ) : null}
        {error ? (
          <p className="quote-create-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="ds-confirmation__actions">
          <button className="master-button" type="button" onClick={onClose}>
            Revenir
          </button>
          <button
            className="master-button master-button--secondary"
            type="button"
            disabled={!ready || pending}
            onClick={() => {
              onConfirm(reason.trim())
            }}
          >
            {pending ? (pendingLabel ?? confirmLabel) : confirmLabel}
          </button>
        </div>
      </div>
    </Drawer>
  )
}
